import { io } from 'socket.io-client';
import { API_BASE_URL } from '../../config';

const DEFAULT_EVENTS = [
  'realtime:force-refetch',
  'realtime:pricing-updated',
  'realtime:inventory-updated',
  'realtime:notifications-updated',
  'realtime:payment-updated'
];

let socket = null;
let started = false;
let connectErrorCount = 0;
let domainVersions = {
  inventoryVersion: 0,
  pricingVersion: 0,
  notificationsVersion: 0,
  paymentVersion: 0
};

function getEnvFlag() {
  try {
    if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_WS_CLIENT_ENABLED === '0') {
      return false;
    }
  } catch (e) {}
  return true;
}

function getLocalFlag() {
  try {
    const value = localStorage.getItem('ws_client_enabled');
    if (value === '0' || value === 'false') return false;
  } catch (e) {}
  return true;
}

function getAutoDisabledFlag() {
  try {
    return sessionStorage.getItem('ws_client_auto_disabled') === '1';
  } catch (e) {}
  return false;
}

function markAutoDisabled() {
  try {
    sessionStorage.setItem('ws_client_auto_disabled', '1');
  } catch (e) {}
}

function clearAutoDisabled() {
  try {
    sessionStorage.removeItem('ws_client_auto_disabled');
  } catch (e) {}
}

function isEnabled() {
  return getEnvFlag() && getLocalFlag() && !getAutoDisabledFlag();
}

function emitWindowEvent(name, detail = {}) {
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

async function sendRefetchLatencyMetric(event) {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const at = Number(event?.at || 0);
    if (!at) return;
    const latencyMs = Math.max(0, Date.now() - at);
    await fetch(`${API_BASE_URL}/api/realtime/observe-refetch-latency`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      credentials: 'include',
      keepalive: true,
      body: JSON.stringify({
        type: String(event?.type || 'unknown'),
        latencyMs
      })
    });
  } catch (e) {
    // Metrics must not affect UX flow.
  }
}

function getDomainKey(event) {
  switch (event?.type) {
    case 'inventory.updated':
      return 'inventoryVersion';
    case 'pricing.updated':
      return 'pricingVersion';
    case 'notifications.updated':
      return 'notificationsVersion';
    case 'payment.intent.updated':
      return 'paymentVersion';
    default:
      return null;
  }
}

function shouldApplyEvent(event) {
  const key = getDomainKey(event);
  if (!key) return true;
  const incoming = Number(event?.[key] || 0);
  const current = Number(domainVersions[key] || 0);
  if (!incoming) return true;
  if (incoming <= current) return false;
  domainVersions = { ...domainVersions, [key]: incoming };
  return true;
}

function emitByType(event, reason) {
  emitWindowEvent('realtime:domain-event', { event, reason });
  switch (event?.type) {
    case 'inventory.updated':
      emitWindowEvent('realtime:inventory-updated', { event, reason });
      break;
    case 'pricing.updated':
      emitWindowEvent('realtime:pricing-updated', { event, reason });
      break;
    case 'notifications.updated':
      emitWindowEvent('realtime:notifications-updated', { event, reason });
      emitWindowEvent('refreshNotifications', { source: 'ws' });
      break;
    case 'payment.intent.updated':
      emitWindowEvent('realtime:payment-updated', { event, reason });
      break;
    default:
      break;
  }
}

function emitForcedRefetch(reason) {
  emitWindowEvent('realtime:force-refetch', { reason });
  // Signal all domains during connect/reconnect to avoid missed events.
  DEFAULT_EVENTS.forEach((evt) => {
    if (evt !== 'realtime:force-refetch') {
      emitWindowEvent(evt, { reason });
    }
  });
}

function getSocketBaseUrl() {
  try {
    const parsed = new URL(API_BASE_URL);
    return parsed.origin;
  } catch (e) {
    return API_BASE_URL;
  }
}

function shouldStopRetrying(error) {
  const message = String(error?.message || '').toLowerCase();
  if (message.includes('unauthorized')) return true;
  if (message.includes('xhr poll error')) return true;
  if (message.includes('websocket error')) return true;
  return false;
}

export function startRealtimeClient() {
  if (started) return socket;
  if (!isEnabled()) return null;

  const token = localStorage.getItem('authToken');
  if (!token) return null;

  socket = io(getSocketBaseUrl(), {
    path: '/socket.io',
    transports: ['websocket', 'polling'],
    auth: { token },
    reconnectionAttempts: 2,
    timeout: 7000
  });

  socket.on('connect', () => {
    clearAutoDisabled();
    connectErrorCount = 0;
    emitForcedRefetch('connect');
  });

  socket.io.on('reconnect', () => {
    emitForcedRefetch('reconnect');
  });

  socket.on('domain_event', (event) => {
    if (!shouldApplyEvent(event)) return;
    sendRefetchLatencyMetric(event);
    emitByType(event, 'domain_event');
  });

  socket.on('connect_error', (error) => {
    connectErrorCount += 1;
    if (shouldStopRetrying(error) || connectErrorCount >= 3) {
      markAutoDisabled();
      stopRealtimeClient();
    }
  });

  started = true;
  return socket;
}

export function stopRealtimeClient() {
  if (socket) {
    socket.close();
  }
  socket = null;
  started = false;
  connectErrorCount = 0;
}
